/*
  Small behavioural additions on top of focal's own header/desktop-menu/
  mobile-menu custom elements (DesktopNavigation, ToggleButton,
  CollapsibleContent in theme.js). Loaded after theme.js and custom.js, the
  same way custom.js itself is loaded after theme.js.
*/

/*
  focal's own DesktopNavigation only intercepts a click to open the panel
  first when the device has no hover capability (window.matchMedia('(hover:
  hover)') does not match). On an ordinary desktop with a mouse present, a
  keyboard user tabbing to a top-level item and pressing Enter gets sent
  straight to the category page, because the click handler treats every
  Enter-triggered click the same as a mouse click on a hover-capable device.
  That leaves the panel unreachable without a mouse.

  This opens the panel on the first Enter instead, purely via keydown (before
  focal's own click handler runs), and only when the panel is not open yet. A
  second Enter, with aria-expanded already "true", falls straight through to
  focal's own handler and navigates -- the same outcome a mouse click gets
  once hover has already opened the panel.
*/
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter') return;

  const link = event.target.closest('.header__linklist-link[aria-expanded]');
  if (!link || link.getAttribute('aria-expanded') === 'true') return;

  const panel = document.getElementById(link.getAttribute('aria-controls'));
  if (!panel) return;

  event.preventDefault();
  link.setAttribute('aria-expanded', 'true');
  panel.removeAttribute('hidden');
});

/*
  mega menu open/close on desktop hover: implemented fresh here instead of
  left to focal's own DesktopNavigation (theme.js), for two independent
  reasons found while verifying this menu against
  docs/superpowers/specs/2026-08-14-chrome-behaviour-spec.md (the Dawn/kludi
  build's own behaviour, read as a specification and re-implemented here, not
  copied from it):

  1. focal's own open path gates on "event.relatedTarget !== null" on
     mouseenter. relatedTarget is null whenever the browser has never tracked
     a hovered element on the page before -- exactly what happens on a real
     visitor's first hover of a session, landing straight on a top-level nav
     item. Confirmed by reproduction (a single, unprimed pointer move
     straight onto the item fires mouseenter with relatedTarget null and
     aria-expanded never flips to "true"): the panel silently never opens,
     only the link's own css underline shows, until the visitor moves the
     mouse away and back a second time
  2. even once open, focal's own close path has no grace period: it closes
     on the very next mouseleave with a non-null relatedTarget. The spec
     calls for a 220ms delay specifically so a pointer moving diagonally from
     the trigger into the panel doesn't cross a gap in the hit-tested DOM
     tree (the moment that happens, mouseleave fires on the header) and lose
     the panel before arriving

  Scope: this only takes over hover-driven open/close, and only under the
  exact gate the spec calls for -- (min-width: 1200px) and (hover: hover) and
  (pointer: fine). Below that, or without a fine pointer, focal's own
  click-to-open-first fallback (theme.js, unrelated code path keyed off
  "hover: hover" alone) still runs untouched, so touch devices are unaffected
*/
(() => {
  const CLOSE_DELAY = 220;
  const desktopHover = () => window.matchMedia('(min-width: 1200px) and (hover: hover) and (pointer: fine)').matches;

  const header = document.querySelector('store-header');
  if (!header) return;

  let closeTimer = null;

  const triggerFor = (item) => item.querySelector(':scope > [aria-controls]');
  const panelFor = (trigger) => document.getElementById(trigger.getAttribute('aria-controls'));
  const openItem = () => {
    const trigger = header.querySelector('.header__linklist-link[aria-expanded="true"]');
    return trigger ? trigger.closest('.header__linklist-item') : null;
  };

  function closeNow(item) {
    const trigger = triggerFor(item);
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', 'false');
    const panel = panelFor(trigger);
    if (panel) panel.setAttribute('hidden', '');
  }

  function openNow(item) {
    const trigger = triggerFor(item);
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', 'true');
    const panel = panelFor(trigger);
    if (panel) panel.removeAttribute('hidden');
  }

  // stop focal's own hover-triggered open/close from double-driving the same
  // trigger while our own gate is active, so its 220ms-less close doesn't
  // undercut the grace period above -- scoped to the same gate, so it only
  // ever intercepts the exact interaction this controller is taking over
  document.addEventListener('mouseenter', (event) => {
    if (!desktopHover()) return;
    if (event.target.closest && event.target.closest('.has-dropdown')) event.stopPropagation();
  }, true);
  document.addEventListener('mouseleave', (event) => {
    if (!desktopHover()) return;
    if (event.target.closest && event.target.closest('.has-dropdown')) event.stopPropagation();
  }, true);

  header.addEventListener('mouseover', (event) => {
    if (!desktopHover()) return;
    const item = event.target.closest('.header__linklist-item');
    if (!item) return;

    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }

    const current = openItem();
    if (item === current) return;
    if (current) closeNow(current);
    openNow(item);
  });

  header.addEventListener('mouseleave', () => {
    if (!desktopHover()) return;
    const current = openItem();
    if (!current) return;

    closeTimer = setTimeout(() => {
      closeTimer = null;
      closeNow(current);
    }, CLOSE_DELAY);
  });
})();

/*
  the mobile drawer's accordions (collapsible-content, toggled by
  is="toggle-button") each open and close independently in focal -- the
  design wants opening one to close its siblings at the same level. Listening
  for focal's own "openable-element:open" event and closing the other
  collapsible-content elements under the same parent list keeps each level
  (top-level categories, and each category's own sub-items) exclusive on its
  own, without touching focal's disclosure mechanics
*/
document.addEventListener('openable-element:open', (event) => {
  const item = event.target.closest('.mobile-nav__item');
  if (!item || !item.parentElement) return;

  for (const sibling of item.parentElement.children) {
    if (sibling === item) continue;
    const openSiblingPanel = sibling.querySelector(':scope > collapsible-content[open]');
    if (openSiblingPanel) openSiblingPanel.open = false;
  }
});

/*
  the drawer's own accordions run focal's shared CollapsibleContent open/close
  animation (theme.js), hardcoded to 500ms -- used by every collapsible in the
  theme (product accordions, faq, filter drawers), so it cannot be edited to
  retime only the drawer's own. The behaviour spec
  (docs/superpowers/specs/2026-08-14-chrome-behaviour-spec.md, read from the
  Dawn/kludi build as a specification, not copied) calls for 300ms and a
  data-closing attribute held for the whole close.

  Rather than reimplementing CollapsibleContent's open/close logic here --
  which would have to duplicate its ignoreNextTransition guard and its
  animate-items opacity pass, and would quietly drift out of sync the moment
  focal's own version changes -- this wraps Element.prototype.animate and
  only ever touches the one call CollapsibleContent makes for the drawer's
  own accordions (matched on tag, ancestry and the exact height keyframe/500ms
  duration it always passes), retiming just that call. Every other animate()
  call on the page, including every other collapsible-content, passes through
  completely untouched
*/
(() => {
  const ACCORDION_DURATION = 300;
  const originalAnimate = Element.prototype.animate;

  Element.prototype.animate = function (keyframes, options) {
    const isDrawerAccordion =
      this.tagName === 'COLLAPSIBLE-CONTENT' &&
      this.closest('#mobile-menu-drawer') &&
      options &&
      options.duration === 500 &&
      keyframes &&
      Object.prototype.hasOwnProperty.call(keyframes, 'height');

    if (!isDrawerAccordion) return originalAnimate.call(this, keyframes, options);

    // set at the moment the close starts, not once it finishes -- the spec's
    // whole point is a caret (or any other closing-state styling) that turns
    // at the start of the close
    const closing = options.direction === 'reverse';
    if (closing) this.setAttribute('data-closing', '');

    const animation = originalAnimate.call(this, keyframes, { ...options, duration: ACCORDION_DURATION });
    if (closing) {
      const clearClosing = () => this.removeAttribute('data-closing');
      animation.addEventListener('finish', clearClosing);
      animation.addEventListener('cancel', clearClosing);
    }
    return animation;
  };
})();
